import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Checkbox, Button, Tooltip, Badge } from 'antd';
import { ReloadOutlined, SettingOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import styles from './ArtifactStateFlowDiagram.module.css';

interface State {
  id: string;
  name: string;
  state_type: string;
  is_initial?: boolean;
  is_final?: boolean;
  is_required?: boolean;
  diagram_x?: number | null;
  diagram_y?: number | null;
}

interface Transition {
  id: string;
  name: string;
  from_state_id: string;
  to_state_id: string;
  from_state_name: string;
  to_state_name: string;
  transition_role: string;
  process_name?: string;
  process_id?: string; // Actual process ID for clicking
  diagram_waypoints?: Array<{x: number, y: number}>;
  diagram_process_x?: number | null;
  diagram_process_y?: number | null;
}

interface ArtifactStateFlowDiagramProps {
  states: State[];
  transitions: Transition[];
  artifactName: string;
  artifactId?: string; // Artifact ID for API calls
  onStateClick?: (stateId: string) => void;
  onTransitionClick?: (transitionId: string) => void;
  onProcessClick?: (processId: string) => void;
  onArtifactClick?: () => void;
  selectedElement?: { type: 'artifact' | 'state' | 'transition' | 'process'; id: string };
  showFilters?: boolean; // Show filter controls
  visibleTransitionTypes?: string[]; // Which transition types to display
  onFilterChange?: (types: string[]) => void; // Callback when filters change
  onDiagramUpdate?: () => void; // Callback when diagram positions are saved
  collapsed?: boolean; // Whether diagram is collapsed
  onCollapseToggle?: () => void; // Callback when collapse button is clicked
}

interface StateNode {
  id: string;
  name: string;
  state_type: string;
  is_initial: boolean;
  is_final: boolean;
  is_required: boolean;
  hasTransitions: boolean;
  incomingTransitions: Transition[];
  outgoingTransitions: Transition[];
  level: number;
  column: number;
  row: number;
  diagram_x: number | null;
  diagram_y: number | null;
  prime_artifact_ref?: string;  // For API calls
}

export const ArtifactStateFlowDiagram: React.FC<ArtifactStateFlowDiagramProps> = ({
  states,
  transitions,
  artifactName,
  artifactId,
  onStateClick,
  onTransitionClick,
  onProcessClick,
  selectedElement,
  onArtifactClick,
  showFilters = true,
  visibleTransitionTypes = ['PROCESSITEM', 'PROCESSITEM_PERSISTENT', 'MATERIAL', 'RESOURCE', 'CREATION'],
  onFilterChange,
  onDiagramUpdate,
  collapsed = false,
  onCollapseToggle
}) => {
  // State management for drag-and-drop (states)
  const [draggedState, setDraggedState] = useState<string | null>(null);
  const [statePositions, setStatePositions] = useState<Record<string, { x: number, y: number }>>({});
  const svgRef = useRef<SVGSVGElement>(null);
  
  // State management for drag-and-drop (waypoints)
  const [draggedWaypoint, setDraggedWaypoint] = useState<{
    transitionId: string;
    waypointIndex: number;
  } | null>(null);
  const [waypointPositions, setWaypointPositions] = useState<Record<string, Array<{x: number, y: number}>>>({});
  
  // State management for drag-and-drop (process circles)
  // Key is processId (not transitionId) since one process can be used by multiple transitions
  const [draggedProcess, setDraggedProcess] = useState<string | null>(null); // processId
  const [processPositions, setProcessPositions] = useState<Record<string, {x: number, y: number}>>({});
  
  // Selection state - selected transition reveals all handles
  const [selectedTransitionId, setSelectedTransitionId] = useState<string | null>(null);
  
  // Hover state for showing connection points
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [hoveredTransition, setHoveredTransition] = useState<string | null>(null);
  const [stateProperties, setStateProperties] = useState<Record<string, {total: number, material: number, linked: number}>>({});
  
  // Create a stable reference to state IDs for dependency checking
  const stateIdsString = useMemo(() => {
    return states.map(s => s.id).sort().join(',');
  }, [states]);

  // Fetch property information for states
  // Use useCallback with stateIdsString to prevent unnecessary recreations
  const fetchStateProperties = useCallback(async () => {
    if (states.length === 0) return;
    
    const propertyData: Record<string, {total: number, material: number, linked: number}> = {};
    
    for (const state of states) {
      try {
        const response = await apiClient.get(`/prime-artifact-states/${state.id}/properties`);
        const data = response.data;
        
        // Combine all property arrays from the response
        const allProperties = [
          ...(data.base_properties || []),
          ...(data.type_properties || []),
          ...(data.state_properties || []),
          ...(data.inherited_properties || [])
        ];
        
        // Material properties are those with material_artifact_ref
        const materialProperties = allProperties.filter((p: any) => p.material_artifact_ref);
        const linkedProperties = materialProperties.filter((p: any) => p.linked_transition_ref);
        
        propertyData[state.id] = {
          total: data.total_count || allProperties.length,
          material: materialProperties.length,
          linked: linkedProperties.length
        };
      } catch (error: any) {
        // Suppress 404 and 500 errors - these are expected for states without properties or backend issues
        // Only log unexpected errors (other status codes)
        const status = error?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error(`Error fetching properties for state ${state.id}:`, error);
        }
        propertyData[state.id] = { total: 0, material: 0, linked: 0 };
      }
    }
    
    setStateProperties(propertyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateIdsString]); // Use stateIdsString instead of states to prevent re-runs on array reference changes
  
  // Fetch properties when state IDs change (not the array reference)
  useEffect(() => {
    if (states.length > 0) {
      fetchStateProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateIdsString]); // Only depend on stateIdsString, not fetchStateProperties
  
  // Initialize positions from props when data changes
  // Use functional updates to merge with existing state instead of replacing
  useEffect(() => {
    // Merge state positions from incoming states (don't overwrite existing)
    setStatePositions(prev => {
      const merged = { ...prev };
      states.forEach(state => {
        if (state.diagram_x !== null && state.diagram_x !== undefined && 
            state.diagram_y !== null && state.diagram_y !== undefined) {
          // Only update if we don't have a local position already, or if position changed in DB
          if (!prev[state.id] || 
              prev[state.id].x !== state.diagram_x || 
              prev[state.id].y !== state.diagram_y) {
            merged[state.id] = { x: state.diagram_x, y: state.diagram_y };
          }
        }
      });
      return merged;
    });

    // Merge waypoint and process positions from incoming transitions
    setWaypointPositions(prev => {
      const merged = { ...prev };
      transitions.forEach(transition => {
        if (transition.diagram_waypoints && Array.isArray(transition.diagram_waypoints)) {
          merged[transition.id] = transition.diagram_waypoints;
        }
      });
      return merged;
    });

    setProcessPositions(prev => {
      const merged = { ...prev };
      transitions.forEach(transition => {
        // Store position by processId (not transitionId) since multiple transitions share one process
        if (transition.process_id && 
            transition.diagram_process_x !== null && transition.diagram_process_x !== undefined &&
            transition.diagram_process_y !== null && transition.diagram_process_y !== undefined) {
          // Use first position found for this process (multiple transitions may have different positions, use first)
          if (!merged[transition.process_id]) {
            merged[transition.process_id] = { 
              x: transition.diagram_process_x, 
              y: transition.diagram_process_y 
            };
          }
        }
      });
      return merged;
    });

      // Removed verbose diagram position log
  }, [states, transitions]);
  
  // Convert browser coordinates to SVG coordinates
  const getSVGPoint = (e: React.MouseEvent): { x: number, y: number } => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: svgP.x, y: svgP.y };
  };
  
  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();
    setDraggedState(nodeId);
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggedState) return;
    const point = getSVGPoint(e);
    setStatePositions(prev => ({
      ...prev,
      [draggedState]: { x: point.x - 60, y: point.y - 15 } // Center on cursor (pill is 120x30)
    }));
  };
  
  const handleMouseUp = async () => {
    if (!draggedState || !artifactId) return;
    const position = statePositions[draggedState];
    if (position) {
      // Save to database
      try {
        await apiClient.patch(
          `/prime-artifacts/${artifactId}/states/${draggedState}/position`,
          position
        );
        console.log('✅ State position saved:', { stateId: draggedState, position });
        // Don't reload - local state already has correct position
      } catch (err) {
        console.error('❌ Failed to save state position:', err);
      }
    }
    setDraggedState(null);
  };
  
  // Waypoint drag handlers
  const handleWaypointMouseDown = (e: React.MouseEvent, transitionId: string, waypointIndex: number) => {
    e.stopPropagation();
    setDraggedWaypoint({ transitionId, waypointIndex });
  };
  
  const handleWaypointMouseMove = (e: React.MouseEvent) => {
    if (!draggedWaypoint) return;
    const point = getSVGPoint(e);
    
    setWaypointPositions(prev => {
      const transition = filteredTransitions.find(t => t.id === draggedWaypoint.transitionId);
      const currentWaypoints = prev[draggedWaypoint.transitionId] || transition?.diagram_waypoints || [];
      const newWaypoints = [...currentWaypoints];
      newWaypoints[draggedWaypoint.waypointIndex] = point;
      return {
        ...prev,
        [draggedWaypoint.transitionId]: newWaypoints
      };
    });
  };
  
  const handleWaypointMouseUp = async () => {
    if (!draggedWaypoint || !artifactId) return;
    const waypoints = waypointPositions[draggedWaypoint.transitionId];
    
    if (waypoints) {
      // Save to database
      try {
        const transition = filteredTransitions.find(t => t.id === draggedWaypoint.transitionId);
        if (transition) {
          await apiClient.patch(
            `/prime-artifacts/${artifactId}/transitions/${transition.id}/waypoints`,
            waypoints
          );
          console.log('✅ Waypoints saved:', { transitionId: transition.id, waypointsCount: waypoints.length });
          // Don't reload - local state already has correct waypoints
        }
      } catch (err) {
        console.error('❌ Failed to save waypoints:', err);
      }
    }
    
    setDraggedWaypoint(null);
  };
  
  // Add waypoint to arrow (shift+click) or select transition (regular click)
  const handleArrowClick = (e: React.MouseEvent, transitionId: string, processId?: string) => {
    if (e.shiftKey && artifactId) {
      e.stopPropagation();
      const point = getSVGPoint(e);
      const transition = filteredTransitions.find(t => t.id === transitionId);
      if (transition) {
        const currentWaypoints = waypointPositions[transitionId] || transition.diagram_waypoints || [];
        const newWaypoints = [...currentWaypoints, point];
        setWaypointPositions(prev => ({
          ...prev,
          [transitionId]: newWaypoints
        }));
        
        // Save immediately
        (async () => {
          try {
            await apiClient.patch(
              `/prime-artifacts/${artifactId}/transitions/${transitionId}/waypoints`,
              newWaypoints
            );
            console.log('✅ Waypoint added:', { transitionId, waypointsCount: newWaypoints.length });
            // Don't reload - local state already has correct waypoints
          } catch (err) {
            console.error('❌ Failed to save waypoint:', err);
          }
        })();
      }
    } else {
      // Select the transition (reveals all handles)
      setSelectedTransitionId(transitionId);
      onTransitionClick?.(transitionId);
    }
  };
  
  // Remove waypoint (double-click)
  const handleWaypointDoubleClick = async (e: React.MouseEvent, transitionId: string, waypointIndex: number) => {
    e.stopPropagation();
    if (!artifactId) return;
    
    const transition = filteredTransitions.find(t => t.id === transitionId);
    if (transition) {
      const currentWaypoints = waypointPositions[transitionId] || transition.diagram_waypoints || [];
      const newWaypoints = currentWaypoints.filter((_, idx) => idx !== waypointIndex);
      setWaypointPositions(prev => ({
        ...prev,
        [transitionId]: newWaypoints
      }));
      
      // Save immediately
      try {
        await apiClient.patch(
          `/prime-artifacts/${artifactId}/transitions/${transitionId}/waypoints`,
          newWaypoints
        );
        console.log('✅ Waypoint removed:', { transitionId, waypointsCount: newWaypoints.length });
        // Don't reload - local state already has correct waypoints
      } catch (err) {
        console.error('❌ Failed to remove waypoint:', err);
      }
    }
  };
  
  // Process drag handlers
  const handleProcessMouseDown = (e: React.MouseEvent, processId: string) => {
    e.stopPropagation();
    setDraggedProcess(processId);
  };
  
  const handleProcessMouseMove = (e: React.MouseEvent) => {
    if (!draggedProcess) return;
    const point = getSVGPoint(e);
    setProcessPositions(prev => ({
      ...prev,
      [draggedProcess]: point
    }));
  };
  
  const handleProcessMouseUp = async () => {
    if (!draggedProcess || !artifactId) return;
    const position = processPositions[draggedProcess];
    
    if (position) {
      // Save to database for ALL transitions that use this process
      try {
        const transitionsWithProcess = filteredTransitions.filter(t => t.process_id === draggedProcess);
        
        // Save position for each transition that uses this process
        const savePromises = transitionsWithProcess.map(transition =>
          apiClient.patch(
            `/prime-artifacts/${artifactId}/transitions/${transition.id}/process-position`,
            position
          )
        );
        
        await Promise.all(savePromises);
        console.log(`✅ Process position saved for ${transitionsWithProcess.length} transition(s):`, { 
          processId: draggedProcess, 
          position,
          transitionIds: transitionsWithProcess.map(t => t.id)
        });
        // Don't reload - local state already has correct position
      } catch (err) {
        console.error('❌ Failed to save process position:', err);
      }
    }
    
    setDraggedProcess(null);
  };
  
  // Filter transitions based on visible types
  const filteredTransitions = useMemo(() => {
    return transitions.filter(t => visibleTransitionTypes.includes(t.transition_role));
  }, [transitions, visibleTransitionTypes]);

  // Organize states into a grid layout (rows and columns)
  const stateNodes = useMemo(() => {
    const nodes: StateNode[] = states.map(state => ({
      id: state.id,
      name: state.name,
      state_type: state.state_type,
      is_initial: state.is_initial || false,
      is_final: state.is_final || false,
      is_required: state.is_required || false,
      hasTransitions: false,
      incomingTransitions: [],
      outgoingTransitions: [],
      level: 0,
      column: 0,
      row: 0,
      diagram_x: state.diagram_x ?? null,
      diagram_y: state.diagram_y ?? null,
      prime_artifact_ref: (state as any).prime_artifact_ref
    }));

    // Build transition relationships using filtered transitions
    filteredTransitions.forEach(transition => {
      const fromNode = nodes.find(n => n.id === transition.from_state_id);
      const toNode = nodes.find(n => n.id === transition.to_state_id);
      
      if (fromNode && toNode) {
        fromNode.hasTransitions = true;
        toNode.hasTransitions = true;
        fromNode.outgoingTransitions.push(transition);
        toNode.incomingTransitions.push(transition);
      }
    });

    // Grid-based layout algorithm
    const assignGridPositions = () => {
      const visited = new Set<string>();
      const grid: Map<number, Map<number, StateNode>> = new Map();
      
      // Find states with no predecessors (root nodes)
      const statesWithPredecessors = new Set(
        nodes
          .filter(node => node.incomingTransitions.length > 0)
          .map(node => node.id)
      );
      
      const statesWithoutPredecessors = nodes.filter(
        node => !statesWithPredecessors.has(node.id)
      );
      
      // Sort: states with successors first, then by name
      statesWithoutPredecessors.sort((a, b) => {
        if (a.outgoingTransitions.length > 0 && b.outgoingTransitions.length === 0) return -1;
        if (a.outgoingTransitions.length === 0 && b.outgoingTransitions.length > 0) return 1;
        return a.name.localeCompare(b.name);
      });
      
      let nextAvailableRow = 0;
      
      // Process each root node
      statesWithoutPredecessors.forEach(rootNode => {
        if (visited.has(rootNode.id)) return;
        
        // Place this state and follow its chain
        const placeState = (node: StateNode, col: number, row: number) => {
          if (visited.has(node.id)) return;
          
          // Ensure row exists in grid
          if (!grid.has(row)) {
            grid.set(row, new Map());
          }
          
          // Find next available row if column is occupied
          let targetRow = row;
          while (grid.get(targetRow)?.has(col)) {
            targetRow++;
          }
          
          // Place the state
          grid.get(targetRow)!.set(col, node);
          node.column = col;
          node.row = targetRow;
          visited.add(node.id);
          
          // Place successors
          if (node.outgoingTransitions.length > 0) {
            const nextCol = col + 1;
            
            // Place each successor
            node.outgoingTransitions.forEach((transition, index) => {
              const toNode = nodes.find(n => n.id === transition.to_state_id);
              if (toNode && !visited.has(toNode.id)) {
                // Successor 1: same row, next column
                // Successor 2: next row, same column as successor 1
                // Successor 3: 2 rows down, same column as successor 1
                const successorRow = targetRow + index;
                placeState(toNode, nextCol, successorRow);
              }
            });
          }
        };
        
        placeState(rootNode, 0, nextAvailableRow);
        
        // Update next available row for next root
        const maxRow = Math.max(...Array.from(grid.keys()));
        nextAvailableRow = maxRow + 2; // Add gap between chains
      });
      
      // Handle any remaining states (disconnected states)
      nodes.forEach(node => {
        if (!visited.has(node.id)) {
          // Place in first available position
          let col = 0;
          let row = nextAvailableRow;
          
          while (grid.has(row) && grid.get(row)?.has(col)) {
            col++;
          }
          
          if (!grid.has(row)) {
            grid.set(row, new Map());
          }
          
          grid.get(row)!.set(col, node);
          node.column = col;
          node.row = row;
          visited.add(node.id);
        }
      });
    };

    assignGridPositions();

    return nodes;
  }, [states, filteredTransitions]);

  // Group states by row
  const statesByRow = useMemo(() => {
    const grouped: { [row: number]: StateNode[] } = {};
    stateNodes.forEach(node => {
      if (!grouped[node.row]) {
        grouped[node.row] = [];
      }
      grouped[node.row].push(node);
    });

    // Sort each row by column (left to right)
    Object.keys(grouped).forEach(row => {
      grouped[parseInt(row)].sort((a, b) => a.column - b.column);
    });

    return grouped;
  }, [stateNodes]);

  // Calculate positions for states in a grid layout
  const getStatePosition = (node: StateNode) => {
    // Priority: actively dragged > saved position > auto-layout
    if (statePositions[node.id]) {
      return statePositions[node.id];
    }
    
    // If node has saved position, use it
    if (node.diagram_x !== null && node.diagram_x !== undefined) {
      return { x: node.diagram_x, y: node.diagram_y! };
    }
    
    // Grid cell dimensions - increased spacing to prevent overlaps
    const cellWidth = 250;  // horizontal spacing between states (left to right) - increased from 200
    const cellHeight = 100; // vertical spacing between rows (top to bottom) - increased from 80
    
    // Calculate position within the grid using row and column
    const x = 100 + node.column * cellWidth;
    const y = 80 + node.row * cellHeight;

    return { x, y };
  };

  // Calculate connection point on state border based on direction to target
  const getConnectionPoint = (
    statePos: { x: number, y: number },
    targetX: number,
    targetY: number,
    pillWidth: number = 120,
    pillHeight: number = 30
  ): { x: number, y: number, side: string } => {
    const stateCenterX = statePos.x + pillWidth / 2;
    const stateCenterY = statePos.y + pillHeight / 2;
    
    // Calculate angle to target
    const dx = targetX - stateCenterX;
    const dy = targetY - stateCenterY;
    const angle = Math.atan2(dy, dx);
    
    // Convert angle to degrees (0-360)
    const degrees = ((angle * 180 / Math.PI) + 360) % 360;
    
    // Determine which side of the rectangle to use
    // Divide into 8 sectors (45 degrees each)
    let x, y, side;
    
    if (degrees >= 337.5 || degrees < 22.5) {
      // East (right)
      x = statePos.x + pillWidth;
      y = stateCenterY;
      side = 'E';
    } else if (degrees >= 22.5 && degrees < 67.5) {
      // Southeast
      x = statePos.x + pillWidth;
      y = statePos.y + pillHeight;
      side = 'SE';
    } else if (degrees >= 67.5 && degrees < 112.5) {
      // South (bottom)
      x = stateCenterX;
      y = statePos.y + pillHeight;
      side = 'S';
    } else if (degrees >= 112.5 && degrees < 157.5) {
      // Southwest
      x = statePos.x;
      y = statePos.y + pillHeight;
      side = 'SW';
    } else if (degrees >= 157.5 && degrees < 202.5) {
      // West (left)
      x = statePos.x;
      y = stateCenterY;
      side = 'W';
    } else if (degrees >= 202.5 && degrees < 247.5) {
      // Northwest
      x = statePos.x;
      y = statePos.y;
      side = 'NW';
    } else if (degrees >= 247.5 && degrees < 292.5) {
      // North (top)
      x = stateCenterX;
      y = statePos.y;
      side = 'N';
    } else {
      // Northeast
      x = statePos.x + pillWidth;
      y = statePos.y;
      side = 'NE';
    }
    
    return { x, y, side };
  };

  // Helper: Check if transition is a self-loop
  const isSelfLoop = (transition: Transition) => {
    return transition.from_state_id === transition.to_state_id;
  };
  
  // Helper: Check if transition is creation
  const isCreation = (transition: Transition) => {
    return transition.transition_role === 'CREATION' || !transition.from_state_id;
  };
  
  // Generate incoming arrow path (state → process) with waypoint support
  const getIncomingArrowPath = (
    fromConnection: {x: number, y: number},
    processPos: {x: number, y: number},
    waypoints: Array<{x: number, y: number}>
  ) => {
    const allPoints = [fromConnection, ...waypoints, processPos];
    return `M ${allPoints.map(p => `${p.x} ${p.y}`).join(' L ')}`;
  };
  
  // Generate outgoing arrow path (process → state) - direct line
  const getOutgoingArrowPath = (
    processPos: {x: number, y: number},
    toConnection: {x: number, y: number}
  ) => {
    return `M ${processPos.x} ${processPos.y} L ${toConnection.x} ${toConnection.y}`;
  };
  
  // Generate self-loop path (state → process → state)
  const getSelfLoopPath = (
    statePos: {x: number, y: number},
    processPos: {x: number, y: number},
    pillWidth: number,
    pillHeight: number
  ) => {
    // Process is positioned below the state
    // Create curved path from bottom of state to process and back
    const stateCenterX = statePos.x + pillWidth / 2;
    const stateBottom = statePos.y + pillHeight;
    
    const outgoing = `M ${stateCenterX} ${stateBottom} Q ${stateCenterX} ${processPos.y} ${processPos.x} ${processPos.y}`;
    const incoming = `M ${processPos.x} ${processPos.y} Q ${stateCenterX} ${processPos.y} ${stateCenterX} ${stateBottom}`;
    
    return { outgoing, incoming };
  };
  
  // Calculate position for process circle - handles standard, self-loop, and creation
  const getProcessPosition = (
    transition: Transition,
    fromNode: StateNode | null | undefined,
    toNode: StateNode
  ) => {
    const pillWidth = 120;
    const pillHeight = 30;
    const toPos = getStatePosition(toNode);
    
    // Self-loop: position process below the state
    if (isSelfLoop(transition)) {
      return {
        x: toPos.x + pillWidth / 2,  // Center horizontally
        y: toPos.y + pillHeight + 40  // 40px below state
      };
    }
    
    // Creation: position process to the left of initial state
    if (!fromNode) {
      return {
        x: toPos.x - 80,  // 80px to the left
        y: toPos.y + pillHeight / 2  // Centered vertically
      };
    }
    
    // Standard transition: position between from and to states
    const fromPos = getStatePosition(fromNode);
    
    // Calculate midpoint between state centers
    return {
      x: (fromPos.x + toPos.x + pillWidth) / 2,  // Midpoint of centers
      y: (fromPos.y + toPos.y + pillHeight) / 2 - 15  // Slightly above midpoint
    };
  };

  // Calculate bounding box around all states (for artifact container)
  const getArtifactBounds = () => {
    if (stateNodes.length === 0) {
      return { minX: 0, maxX: 200, minY: 0, maxY: 100, width: 200, height: 100 };
    }
    
    const positions = stateNodes.map(getStatePosition);
    const minX = Math.min(...positions.map(p => p.x)) - 30;
    const maxX = Math.max(...positions.map(p => p.x + 120)) + 30;
    const minY = Math.min(...positions.map(p => p.y)) - 50; // Extra space for header
    const maxY = Math.max(...positions.map(p => p.y + 30)) + 20;
    
    return { 
      minX, 
      maxX, 
      minY, 
      maxY, 
      width: maxX - minX, 
      height: maxY - minY 
    };
  };
  
  const artifactBounds = getArtifactBounds();

  // Calculate actual content bounds (for SVG viewBox)
  const calculateContentBounds = () => {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    stateNodes.forEach(node => {
      const position = getStatePosition(node);
      const pillWidth = 120;
      const pillHeight = 30;
      
      minX = Math.min(minX, position.x);
      maxX = Math.max(maxX, position.x + pillWidth);
      minY = Math.min(minY, position.y);
      maxY = Math.max(maxY, position.y + pillHeight);
    });

    return { minX, maxX, minY, maxY };
  };

  const bounds = calculateContentBounds();
  const viewBoxWidth = Math.max(600, bounds.maxX - bounds.minX + 240); // Add padding for new spacing
  const viewBoxHeight = Math.max(200, bounds.maxY - bounds.minY + 140); // Add padding

  if (states.length === 0) {
    return (
      <div className={`${styles.stateFlowDiagram} ${styles.empty}`}>
        <p>No states defined for this artifact</p>
      </div>
    );
  }

  return (
    <div className={styles.stateFlowDiagram}>
      <div className={styles.diagramHeader}>
        <div className={styles.diagramTitleRow}>
          <h3>State Flow: {artifactName}</h3>
          <div className={styles.diagramHeaderActions}>
            {onCollapseToggle && (
              <Tooltip title={collapsed ? "Expand diagram" : "Collapse diagram"}>
                <Button
                  type="text"
                  size="small"
                  icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                  onClick={onCollapseToggle}
                />
              </Tooltip>
            )}
            {onDiagramUpdate && (
              <Tooltip title="Refresh diagram data">
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    console.log('🔄 Manual diagram refresh requested');
                    onDiagramUpdate();
                  }}
                  size="small"
                  type="default"
                >
                  Refresh
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {!collapsed && (
        <div className={styles.diagramLegend}>
          <div className={styles.legendSection}>
            <span className={styles.legendLabel}>States:</span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.initial}`}></span> Initial
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.normal}`}></span> Normal
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendColor} ${styles.final}`}></span> Final
            </span>
          </div>
          <div className={styles.legendSection}>
            <span className={styles.legendLabel}>Border:</span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendBorder} ${styles.required}`}></span> Required
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendBorder} ${styles.optional}`}></span> Optional
            </span>
          </div>
          
          <div className={styles.legendSection}>
            <span className={styles.legendLabel}>Process:</span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.processitem}`}></span> Process
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.material}`}></span> Material
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.resource}`}></span> Resource
            </span>
            <span className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.creation}`}></span> Creation
            </span>
          </div>
          
          {showFilters && (
            <div className={styles.legendSection}>
              <span className={styles.legendLabel}>Show:</span>
              <Checkbox
                checked={visibleTransitionTypes.includes('PROCESSITEM')}
                onChange={(e) => {
                  const newTypes = e.target.checked 
                    ? [...visibleTransitionTypes, 'PROCESSITEM', 'PROCESSITEM_PERSISTENT']
                    : visibleTransitionTypes.filter(t => t !== 'PROCESSITEM' && t !== 'PROCESSITEM_PERSISTENT');
                  onFilterChange?.(newTypes);
                }}
              >
                Process
              </Checkbox>
              <Checkbox
                checked={visibleTransitionTypes.includes('CREATION')}
                onChange={(e) => {
                  const newTypes = e.target.checked 
                    ? [...visibleTransitionTypes, 'CREATION']
                    : visibleTransitionTypes.filter(t => t !== 'CREATION');
                  onFilterChange?.(newTypes);
                }}
              >
                Creation
              </Checkbox>
              <Checkbox
                checked={visibleTransitionTypes.includes('MATERIAL')}
                onChange={(e) => {
                  const newTypes = e.target.checked 
                    ? [...visibleTransitionTypes, 'MATERIAL']
                    : visibleTransitionTypes.filter(t => t !== 'MATERIAL');
                  onFilterChange?.(newTypes);
                }}
              >
                Material
              </Checkbox>
              <Checkbox
                checked={visibleTransitionTypes.includes('RESOURCE')}
                onChange={(e) => {
                  const newTypes = e.target.checked 
                    ? [...visibleTransitionTypes, 'RESOURCE']
                    : visibleTransitionTypes.filter(t => t !== 'RESOURCE');
                  onFilterChange?.(newTypes);
                }}
              >
                Resource
              </Checkbox>
            </div>
          )}
        </div>
        )}
      </div>

      {!collapsed && (
        <>
          <div className={styles.diagramContainer}>
        <svg
          ref={svgRef}
          className={styles.stateFlowSvg}
          viewBox={`${bounds.minX - 100} ${bounds.minY - 50} ${viewBoxWidth} ${viewBoxHeight}`}
          width={viewBoxWidth}
          height={viewBoxHeight}
          style={{ minWidth: `${viewBoxWidth}px`, minHeight: `${viewBoxHeight}px` }}
          onMouseMove={(e) => {
            handleMouseMove(e);
            handleWaypointMouseMove(e);
            handleProcessMouseMove(e);
          }}
          onMouseUp={() => {
            handleMouseUp();
            handleWaypointMouseUp();
            handleProcessMouseUp();
          }}
        >
          {/* Artifact Container (render first, behind everything) */}
          <g className="artifact-container">
            <rect
              x={artifactBounds.minX}
              y={artifactBounds.minY}
              width={artifactBounds.width}
              height={artifactBounds.height}
              rx="10"
              className="artifact-box"
              onClick={() => onArtifactClick?.()}
              style={{ cursor: onArtifactClick ? 'pointer' : 'default' }}
            />
            <text
              x={artifactBounds.minX + 10}
              y={artifactBounds.minY + 20}
              className="artifact-name-label"
              onClick={() => onArtifactClick?.()}
              style={{ cursor: onArtifactClick ? 'pointer' : 'default' }}
            >
              {artifactName}
            </text>
          </g>
          
          {/* Render states (foreground layer) */}
          {stateNodes.map(node => {
            const position = getStatePosition(node);
            const properties = stateProperties[node.id];

            return (
              <g key={node.id} className="state-node">
                <rect
                  data-manual-position={node.diagram_x !== null}
                  x={position.x}
                  y={position.y}
                  width="120"
                  height="30"
                  rx="15"
                  className={`state-pill ${draggedState === node.id ? 'dragging' : ''} ${node.is_initial ? 'initial-state' : node.is_final ? 'final-state' : 'normal-state'} ${node.is_required ? 'required-state' : 'optional-state'} ${!node.hasTransitions ? 'no-transitions' : ''} ${selectedElement?.type === 'state' && selectedElement.id === node.id ? 'selected' : ''}`}
                  onClick={() => onStateClick?.(node.id)}
                  onMouseDown={(e) => handleMouseDown(e, node.id)}
                  onMouseEnter={() => setHoveredState(node.id)}
                  onMouseLeave={() => setHoveredState(null)}
                  style={{ cursor: draggedState === node.id ? 'grabbing' : 'grab' }}
                />
                <text
                  x={position.x + 60}
                  y={position.y + 20}
                  textAnchor="middle"
                  className="state-name"
                  onClick={() => onStateClick?.(node.id)}
                  style={{ cursor: onStateClick ? 'pointer' : 'default', pointerEvents: 'none' }}
                >
                  {node.name}
                </text>
                
                {/* Property badges */}
                {stateProperties[node.id] && (
                  <g className="property-badges">
                    {/* Total properties badge */}
                    {stateProperties[node.id].total > 0 && (
                      <circle
                        cx={position.x + 110}
                        cy={position.y + 8}
                        r="8"
                        fill="#1890ff"
                        stroke="#fff"
                        strokeWidth="1"
                      />
                    )}
                    {stateProperties[node.id].total > 0 && (
                      <text
                        x={position.x + 110}
                        y={position.y + 12}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="10"
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {stateProperties[node.id].total}
                      </text>
                    )}
                    
                    {/* Material properties badge */}
                    {stateProperties[node.id].material > 0 && (
                      <circle
                        cx={position.x + 110}
                        cy={position.y + 22}
                        r="8"
                        fill={stateProperties[node.id].linked === stateProperties[node.id].material ? "#52c41a" : "#faad14"}
                        stroke="#fff"
                        strokeWidth="1"
                      />
                    )}
                    {stateProperties[node.id].material > 0 && (
                      <text
                        x={position.x + 110}
                        y={position.y + 26}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="10"
                        fontWeight="bold"
                        style={{ pointerEvents: 'none' }}
                      >
                        {stateProperties[node.id].material}
                      </text>
                    )}
                  </g>
                )}
                
                {/* Property tooltip */}
                {properties && (properties.total > 0 || properties.material > 0) && (
                  <title>
                    {`Properties: ${properties.total} total, ${properties.material} material`}
                    {properties.material > 0 && (
                      ` (${properties.linked}/${properties.material} linked)`
                    )}
                  </title>
                )}
              </g>
            );
          })}

          {/* Render transitions on top (foreground layer) - TWO-ARROW PROCESS-CENTRIC */}
          {filteredTransitions.map(transition => {
            const toNode = stateNodes.find(n => n.id === transition.to_state_id);
            if (!toNode) return null;
            
            const pillWidth = 120;
            const pillHeight = 30;
            const toPos = getStatePosition(toNode);
            
            const fromNode = transition.from_state_id ? stateNodes.find(n => n.id === transition.from_state_id) : undefined;
            const selfLoop = isSelfLoop(transition);
            const creation = isCreation(transition);
            
            // Calculate process position - use shared position by processId
            let processPos;
            if (transition.process_id && processPositions[transition.process_id]) {
              // Use shared process position (may be actively dragging)
              processPos = processPositions[transition.process_id];
            } else if (transition.diagram_process_x !== null && transition.diagram_process_x !== undefined) {
              // Use saved position from this transition
              processPos = { x: transition.diagram_process_x, y: transition.diagram_process_y! };
            } else {
              // Auto-calculate for this transition
              processPos = getProcessPosition(transition, fromNode, toNode);
            }
            
            const isSelected = selectedTransitionId === transition.id;
            const isTransitionSelected = selectedElement?.type === 'transition' && selectedElement.id === transition.id;
            const isHovered = hoveredTransition === transition.id;
            const showConnectionPoints = isSelected || isHovered || (fromNode && hoveredState === fromNode.id) || hoveredState === toNode.id;
            
            // Build waypoints array for incoming arrow (use saved/dragged or auto-generate)
            let waypoints: Array<{x: number, y: number}> = [];
            const activeWaypoints = waypointPositions[transition.id];
            const savedWaypoints = transition.diagram_waypoints;
            
            if (!selfLoop && !creation && fromNode) {
              const fromPos = getStatePosition(fromNode);
              if (activeWaypoints) {
                waypoints = activeWaypoints;
              } else if (savedWaypoints && savedWaypoints.length > 0) {
                waypoints = savedWaypoints;
              } else {
                // Auto-generate one waypoint for stepped routing
                if (toPos.x > fromPos.x) {
                  waypoints = [{ x: processPos.x, y: fromPos.y + pillHeight / 2 }];
                } else {
                  waypoints = [{ x: processPos.x, y: toPos.y + pillHeight / 2 }];
                }
              }
            }
            
            const hasCustomWaypoints = savedWaypoints && savedWaypoints.length > 0;

            return (
              <g key={transition.id} className="transition-group">
                {/* SELF-LOOP RENDERING */}
                {selfLoop && fromNode && (
                  <>
                    {(() => {
                      const statePos = getStatePosition(fromNode);
                      const paths = getSelfLoopPath(statePos, processPos, pillWidth, pillHeight);
                      return (
                        <>
                          <path
                            d={paths.outgoing}
                            className={`self-loop-arrow outgoing role-${transition.transition_role.toLowerCase()} ${isTransitionSelected ? 'selected' : ''}`}
                            stroke="currentColor"
                            fill="none"
                            markerEnd="url(#arrowhead)"
                            onClick={(e) => handleArrowClick(e, transition.id, transition.process_id)}
                            onMouseEnter={() => setHoveredTransition(transition.id)}
                            onMouseLeave={() => setHoveredTransition(null)}
                            style={{ cursor: 'pointer' }}
                          />
                          <path
                            d={paths.incoming}
                            className={`self-loop-arrow incoming role-${transition.transition_role.toLowerCase()} ${isTransitionSelected ? 'selected' : ''}`}
                            stroke="currentColor"
                            fill="none"
                            onClick={(e) => handleArrowClick(e, transition.id, transition.process_id)}
                            onMouseEnter={() => setHoveredTransition(transition.id)}
                            onMouseLeave={() => setHoveredTransition(null)}
                            style={{ cursor: 'pointer' }}
                          />
                        </>
                      );
                    })()}
                  </>
                )}
                
                {/* CREATION RENDERING (process → state only) */}
                {creation && !selfLoop && (
                  <>
                    {(() => {
                      const endConnection = getConnectionPoint(
                        toPos,
                        processPos.x,
                        processPos.y,
                        pillWidth,
                        pillHeight
                      );
                      const pathD = getOutgoingArrowPath(processPos, endConnection);
                      
                      return (
                        <path
                          d={pathD}
                          className={`creation-arrow outgoing role-${transition.transition_role.toLowerCase()} ${isTransitionSelected ? 'selected' : ''}`}
                          markerEnd="url(#arrowhead)"
                          onClick={(e) => handleArrowClick(e, transition.id, transition.process_id)}
                          onMouseEnter={() => setHoveredTransition(transition.id)}
                          onMouseLeave={() => setHoveredTransition(null)}
                          style={{ cursor: 'pointer' }}
                        />
                      );
                    })()}
                  </>
                )}
                
                {/* STANDARD RENDERING (state → process → state) */}
                {!selfLoop && !creation && fromNode && (
                  <>
                    {(() => {
                      const fromPos = getStatePosition(fromNode);
                      
                      // Incoming arrow: fromState → process (with waypoints)
                      const startConnection = getConnectionPoint(
                        fromPos,
                        waypoints.length > 0 ? waypoints[0].x : processPos.x,
                        waypoints.length > 0 ? waypoints[0].y : processPos.y,
                        pillWidth,
                        pillHeight
                      );
                      const incomingPath = getIncomingArrowPath(startConnection, processPos, waypoints);
                      
                      // Outgoing arrow: process → toState (direct)
                      const endConnection = getConnectionPoint(
                        toPos,
                        processPos.x,
                        processPos.y,
                        pillWidth,
                        pillHeight
                      );
                      const outgoingPath = getOutgoingArrowPath(processPos, endConnection);
                      
                      return (
                        <>
                          {/* Incoming arrow (state → process) */}
                          <path
                            data-has-waypoints={hasCustomWaypoints}
                            d={incomingPath}
                            className={`incoming-arrow role-${transition.transition_role.toLowerCase()} ${isTransitionSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                            onClick={(e) => handleArrowClick(e, transition.id, transition.process_id)}
                            onMouseEnter={() => setHoveredTransition(transition.id)}
                            onMouseLeave={() => setHoveredTransition(null)}
                            style={{ cursor: 'pointer' }}
                          />
                          
                          {/* Outgoing arrow (process → state) */}
                          <path
                            d={outgoingPath}
                            className={`outgoing-arrow role-${transition.transition_role.toLowerCase()} ${isTransitionSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                            markerEnd="url(#arrowhead)"
                            onClick={(e) => handleArrowClick(e, transition.id, transition.process_id)}
                            onMouseEnter={() => setHoveredTransition(transition.id)}
                            onMouseLeave={() => setHoveredTransition(null)}
                            style={{ cursor: 'pointer' }}
                          />
                          
                          {/* Connection point indicators */}
                          {showConnectionPoints && (
                            <>
                              <circle
                                cx={startConnection.x}
                                cy={startConnection.y}
                                r={isSelected ? "4" : "3"}
                                className="connection-point start"
                                fill={isSelected ? "#3b82f6" : "#6b7280"}
                              />
                              <circle
                                cx={processPos.x}
                                cy={processPos.y}
                                r="5"
                                className="connection-point process"
                                fill="#f59e0b"
                              />
                              <circle
                                cx={endConnection.x}
                                cy={endConnection.y}
                                r={isSelected ? "4" : "3"}
                                className="connection-point end"
                                fill={isSelected ? "#3b82f6" : "#6b7280"}
                              />
                            </>
                          )}
                          
                          {/* Waypoint handles (on incoming arrow only) */}
                          {(isSelected || waypoints.length > 0) && waypoints.map((wp, idx) => {
                            const isFirstOrLast = idx === 0 || idx === waypoints.length - 1;
                            const isDragging = draggedWaypoint?.transitionId === transition.id && draggedWaypoint?.waypointIndex === idx;
                            
                            return (
                              <circle
                                key={`wp-${transition.id}-${idx}`}
                                cx={wp.x}
                                cy={wp.y}
                                r={isFirstOrLast && isSelected ? "7" : "5"}
                                className={`waypoint-handle ${isDragging ? 'dragging' : ''} ${isFirstOrLast ? 'endpoint' : ''}`}
                                fill={isFirstOrLast && isSelected ? "#2563eb" : "#3b82f6"}
                                stroke="#fff"
                                strokeWidth={isSelected ? "3" : "2"}
                                onMouseDown={(e) => handleWaypointMouseDown(e, transition.id, idx)}
                                onDoubleClick={(e) => handleWaypointDoubleClick(e, transition.id, idx)}
                                style={{ cursor: 'grab' }}
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                  </>
                )}
              </g>
            );
          })}
          
          {/* Render process circles (on top of transitions) - ONE CIRCLE PER UNIQUE PROCESS */}
          {(() => {
            // Group transitions by process_id to render each process once
            const processByIdMap = new Map<string, {
              processId: string;
              processName: string;
              transitions: Transition[];
              roleClass: string;
            }>();
            
            filteredTransitions.forEach(transition => {
              if (!transition.process_id || !transition.process_name) return;
              
              if (!processByIdMap.has(transition.process_id)) {
                processByIdMap.set(transition.process_id, {
                  processId: transition.process_id,
                  processName: transition.process_name,
                  transitions: [],
                  roleClass: transition.transition_role.toLowerCase()
                });
              }
              processByIdMap.get(transition.process_id)!.transitions.push(transition);
            });
            
            // Render each unique process
            return Array.from(processByIdMap.values()).map(processGroup => {
              const { processId, processName, transitions: processTransitions, roleClass } = processGroup;
              
              // Calculate process position (use manual position if any transition has it, else auto-calculate)
              let processPos;
              
              if (processPositions[processId]) {
                // Currently being dragged
                processPos = processPositions[processId];
              } else {
                // Check if any transition has a saved position
                const transitionWithPosition = processTransitions.find(t => 
                  t.diagram_process_x !== null && t.diagram_process_x !== undefined
                );
                
                if (transitionWithPosition) {
                  processPos = { 
                    x: transitionWithPosition.diagram_process_x!, 
                    y: transitionWithPosition.diagram_process_y! 
                  };
                } else {
                  // Auto-calculate based on all connected states (use centroid)
                  const allStates: StateNode[] = [];
                  processTransitions.forEach(t => {
                    const fromNode = t.from_state_id ? stateNodes.find(n => n.id === t.from_state_id) : null;
                    const toNode = stateNodes.find(n => n.id === t.to_state_id);
                    if (fromNode) allStates.push(fromNode);
                    if (toNode) allStates.push(toNode);
                  });
                  
                  if (allStates.length > 0) {
                    const pillWidth = 120;
                    const pillHeight = 30;
                    const sumX = allStates.reduce((sum, s) => sum + getStatePosition(s).x + pillWidth / 2, 0);
                    const sumY = allStates.reduce((sum, s) => sum + getStatePosition(s).y + pillHeight / 2, 0);
                    processPos = {
                      x: sumX / allStates.length,
                      y: sumY / allStates.length
                    };
                  } else {
                    processPos = { x: 400, y: 200 }; // Default fallback
                  }
                }
              }
              
              const isProcessSelected = selectedElement?.type === 'process' && selectedElement.id === processId;
              const isDragging = draggedProcess === processId;
              const hasAnyTransitionSelected = processTransitions.some(t => selectedTransitionId === t.id);
              const hasManualPosition = processTransitions.some(t => t.diagram_process_x !== null);
              
              // Build detailed tooltip
              const tooltipLines = [
                processName,
                `${processTransitions.length} transition(s)`,
                ...processTransitions.map(t => `  • ${t.name}`)
              ];
              if (hasManualPosition) {
                tooltipLines.push('(Manually positioned)');
              }
              
              return (
                <g key={`process-${processId}`} className="process-node">
                  {/* Process circle with color coding */}
                  <circle
                    cx={processPos.x}
                    cy={processPos.y}
                    r={hasAnyTransitionSelected || isDragging ? "14" : "12"}
                    className={`process-circle role-${roleClass} ${isProcessSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${hasAnyTransitionSelected ? 'transition-selected' : ''}`}
                    onClick={() => {
                      onProcessClick?.(processId);
                    }}
                    onMouseDown={(e) => handleProcessMouseDown(e, processId)}
                    style={{ cursor: 'grab' }}
                    data-manual-position={hasManualPosition}
                  />
                  
                  {/* Enhanced tooltip */}
                  <title>{tooltipLines.join('\n')}</title>
                  
                  {/* Process label (visible on hover or when selected) */}
                  {(hasAnyTransitionSelected || isProcessSelected) && (
                    <text
                      x={processPos.x}
                      y={processPos.y + 25}
                      textAnchor="middle"
                      className="process-label"
                      style={{ pointerEvents: 'none' }}
                    >
                      {processName}
                    </text>
                  )}
                </g>
              );
            });
          })()}
          
          {/* Render creation starting points (visual indicators) */}
          {filteredTransitions.filter(t => t.transition_role === 'CREATION').map(transition => {
            const toNode = stateNodes.find(n => n.id === transition.to_state_id);
            if (!toNode) return null;
            
            const toPos = getStatePosition(toNode);
            const startX = toPos.x - 150;
            const startY = toPos.y + 15; // Center vertically on the pill
            
            return (
              <g key={`creation-start-${transition.id}`} className="creation-start">
                <circle
                  cx={startX}
                  cy={startY}
                  r="8"
                  fill="#8b5cf6"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  x={startX}
                  y={startY + 4}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="12"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none' }}
                >
                  +
                </text>
                <title>Creation starting point</title>
              </g>
            );
          })}

          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3, 0 6"
                className="arrow-marker"
              />
            </marker>
          </defs>
        </svg>
          </div>

          <div className={styles.diagramFooter}>
            <p className={styles.diagramNote}>
              {transitions.length === 0 
                ? "No transitions defined. States are not connected."
                : `Showing ${filteredTransitions.length} of ${transitions.length} transition${transitions.length !== 1 ? 's' : ''}.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
};



